<template>
  <HermesDrawer
    :model-value="modelValue"
    title="Issue licence"
    :close-on-scrim="!issued"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="onClose"
  >
    <template #header>
      <div>
        <h3 class="h-ilg__title">Issue licence</h3>
        <div class="h-ilg__sub">Grant a licence directly — no checkout, no billing.</div>
      </div>
    </template>

    <!-- ===== Success state — one-time activation token ===== -->
    <div v-if="issued" class="h-ilg">
      <div class="h-ilg__done">
        <div class="h-ilg__doneic">
          <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div>
          <div class="h-ilg__donet">{{ grantLabel(form.grant_type) }} licence issued</div>
          <div class="h-ilg__doned">Subscription #{{ issued.id }} · {{ form.customer_email }}</div>
        </div>
      </div>

      <label class="h-ilg__label" style="margin-top:6px">Activation token</label>
      <HermesApiKeyRevealField
        :value="issuedToken"
        label=""
        :start-revealed="true"
        :shown-once="true"
        warning="Copy this now — it is shown once and never again. The consuming product exchanges it for a signed licence on each heartbeat."
      />

      <div v-if="form.single_use" class="h-ilg__callout h-ilg__callout--info">
        <b>Single-use licence.</b> It binds to the first machine that heartbeats with it and is rejected on any
        other host. To move it to a new machine, open the subscription and regenerate the activation token —
        that releases the binding so the next machine can claim it.
      </div>

      <div class="h-ilg__doneactions">
        <button type="button" class="h-btn h-btn--ghost" @click="reset">Issue another</button>
        <button type="button" class="h-btn h-btn--primary" @click="viewSubscription">View subscription</button>
      </div>
    </div>

    <!-- ===== Form ===== -->
    <div v-else class="h-ilg">
      <!-- Grant type -->
      <div class="h-ilg__field">
        <label class="h-ilg__label">Grant type</label>
        <div class="h-ilg__grants">
          <label
            v-for="g in grantTypes"
            :key="g.value"
            class="h-ilg__grant"
            :class="{ on: form.grant_type === g.value }"
          >
            <input type="radio" :value="g.value" v-model="form.grant_type" />
            <span class="h-ilg__grantb">
              <b>{{ g.label }}</b>
              <small>{{ g.help }}</small>
            </span>
          </label>
        </div>
      </div>

      <!-- Plan -->
      <FormKit
        type="select"
        label="Plan"
        v-model="form.plan_id"
        :options="planOptions"
        :help="planHelp"
      />

      <div class="h-ilg__grid2">
        <FormKit
          type="email"
          label="Customer email"
          v-model="form.customer_email"
          validation="required|email"
          :validation-messages="{ required: 'A customer email is required.', email: 'Enter a valid email.' }"
          help="Finds or creates the end user for your vendor."
        />
        <FormKit
          type="text"
          label="Customer name"
          v-model="form.customer_name"
          help="Optional."
        />
      </div>

      <!-- Expiry — trial / comp only -->
      <div v-if="needsExpiry" class="h-ilg__field">
        <label class="h-ilg__label">Expires on <span class="h-ilg__req">*</span></label>
        <input class="h-ilg__input" type="date" v-model="form.expires_on" :min="minDate" />
        <p class="h-ilg__hint">
          {{ form.grant_type === 'trial'
            ? 'The trial key stops refreshing at end of this day unless converted.'
            : 'The comp licence lapses at end of this day. Extend it later to renew.' }}
        </p>
      </div>
      <div v-else class="h-ilg__callout">
        <b>Lifetime</b> never expires (no billing date). It still heartbeats on the normal 24h cycle —
        it stays revocable and keeps reporting usage.
      </div>

      <!-- Single use -->
      <label class="h-ilg__switch">
        <input type="checkbox" v-model="form.single_use" />
        <span class="h-ilg__track"></span>
        <span class="h-ilg__swlbl">Single-use (bind to one machine)</span>
      </label>
      <p class="h-ilg__hint" style="margin-top:-4px">
        The licence latches onto the first machine that heartbeats and is hard-rejected on any other host.
        Regenerating its activation token (from the subscription page) releases the binding so it can move machines.
      </p>

      <!-- Note -->
      <FormKit
        type="textarea"
        label="Note"
        v-model="form.note"
        :rows="2"
        help="Optional — stored on the subscription for your reference."
      />

      <p v-if="error" class="h-ilg__saveerr">{{ error }}</p>
    </div>

    <!-- ===== Footer ===== -->
    <template #footer>
      <template v-if="!issued">
        <button type="button" class="h-btn h-btn--ghost" @click="$emit('update:modelValue', false)">Cancel</button>
        <button type="button" class="h-btn h-btn--primary" :disabled="saving || !canSubmit" @click="submit">
          <span v-if="saving" class="h-ilg__spin"></span>
          {{ saving ? 'Issuing…' : 'Issue licence' }}
        </button>
      </template>
      <button v-else type="button" class="h-btn h-btn--primary" @click="$emit('update:modelValue', false)">Done</button>
    </template>
  </HermesDrawer>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesIssueLicenceDrawer — admin manual licence issuance. Opens from the
// subscriptions-list header. Posts to /admin/subscriptions/manual (grant_type,
// plan_id, customer_email, customer_name?, expires_at?, single_use?, note?), then
// reveals the one-time activation token via HermesApiKeyRevealField with a link
// through to the new subscription. Trial/comp show an expiry date; lifetime hides
// it. Single-use surfaces the one-machine-binding copy + the regenerate escape hatch.
export default {
  name: 'HermesIssueLicenceDrawer',
  props: {
    modelValue: { type: Boolean, default: false },
    apiBase: { type: String, default: '/api/v1' },
  },
  emits: ['update:modelValue', 'issued'],
  data() {
    return {
      api: useApi(),
      form: this.blankForm(),
      plans: [],
      loadingPlans: false,
      saving: false,
      error: '',
      issued: null,      // subscription object on success
      issuedToken: '',   // one-time plaintext
      grantTypes: [
        { value: 'trial', label: 'Trial', help: 'Time-limited. JWT status trialing; lapses at expiry unless converted.' },
        { value: 'comp', label: 'Comp', help: 'Complimentary active licence with a chosen end date (renewable).' },
        { value: 'lifetime', label: 'Lifetime', help: 'Never expires. Still heartbeats — revocable, usage intact.' },
      ],
    };
  },
  computed: {
    needsExpiry() {
      return this.form.grant_type === 'trial' || this.form.grant_type === 'comp';
    },
    minDate() {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    },
    planOptions() {
      const opts = this.plans.map((p) => ({
        label: `${p.product_name ? p.product_name + ' · ' : ''}${p.name}`,
        value: p.id,
      }));
      if (!opts.length) return [{ label: this.loadingPlans ? 'Loading plans…' : 'No licenced plans', value: '' }];
      return [{ label: 'Select a plan…', value: '' }, ...opts];
    },
    planHelp() {
      return 'Only licenced (activation-issuing) plans can grant a licence.';
    },
    canSubmit() {
      if (!this.form.plan_id) return false;
      if (!this.form.customer_email || !/.+@.+\..+/.test(this.form.customer_email)) return false;
      if (this.needsExpiry && !this.form.expires_on) return false;
      return true;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.reset();
        this.loadPlans();
      }
    },
  },
  methods: {
    blankForm() {
      return {
        grant_type: 'trial',
        plan_id: '',
        customer_email: '',
        customer_name: '',
        expires_on: '',
        single_use: false,
        note: '',
      };
    },
    reset() {
      this.form = this.blankForm();
      this.issued = null;
      this.issuedToken = '';
      this.error = '';
      this.saving = false;
    },
    onClose() {
      // Nothing persisted client-side; parent controls visibility.
    },
    async loadPlans() {
      this.loadingPlans = true;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/plans`);
        this.plans = (data.items || []).filter(
          (p) => p.type === 'licenced_subscription' && p.is_active !== false && p.is_active !== 0,
        );
      } catch (e) {
        this.error = `Couldn't load plans. ${e?.message || ''}`.trim();
      } finally {
        this.loadingPlans = false;
      }
    },
    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      this.error = '';

      const payload = {
        grant_type: this.form.grant_type,
        plan_id: Number(this.form.plan_id),
        customer_email: this.form.customer_email.trim(),
        single_use: !!this.form.single_use,
      };
      if (this.form.customer_name.trim()) payload.customer_name = this.form.customer_name.trim();
      if (this.form.note.trim()) payload.note = this.form.note.trim();
      if (this.needsExpiry) {
        // End of the selected day, as unix seconds.
        payload.expires_at = Math.floor(new Date(`${this.form.expires_on}T23:59:59`).getTime() / 1000);
      }

      try {
        const data = await this.api.post(`${this.apiBase}/admin/subscriptions/manual`, payload);
        this.issued = data.subscription;
        this.issuedToken = data.activation_token_plaintext_once || '';
        this.$emit('issued', this.issued);
      } catch (e) {
        this.error = this.errMsg(e);
      } finally {
        this.saving = false;
      }
    },
    viewSubscription() {
      const id = this.issued?.id;
      if (!id) return;
      this.$emit('update:modelValue', false);
      if (this.$router) this.$router.push(`/admin/subscriptions/${id}`);
      else window.location.href = `/admin/subscriptions/${id}`;
    },
    grantLabel(g) {
      return { trial: 'Trial', comp: 'Comp', lifetime: 'Lifetime' }[g] || g;
    },
    errMsg(e) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.message;
      if (serverMsg) return serverMsg;
      return `Failed to issue licence${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
  },
  mounted() {
    if (this.modelValue) this.loadPlans();
  },
};
</script>

<style scoped>
.h-ilg { font-family: var(--h-font-body, system-ui); }
.h-ilg__title { margin: 0; font-size: 15px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-ilg__sub { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }

.h-ilg__field { margin-bottom: 16px; }
.h-ilg__label { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-ilg__req { color: var(--h-danger, #EF4444); margin-left: 2px; }
.h-ilg__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 16px; line-height: 1.5; }
.h-ilg__grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

/* grant type radio cards */
.h-ilg__grants { display: flex; flex-direction: column; gap: 8px; }
.h-ilg__grant { display: flex; align-items: flex-start; gap: 10px; padding: 11px 13px; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); cursor: pointer; }
.h-ilg__grant.on { border-color: var(--h-accent, #0EA5E9); background: var(--h-accent-light, #E0F2FE); }
.h-ilg__grant input { margin-top: 3px; flex: none; }
.h-ilg__grantb b { display: block; font-size: 13px; color: var(--h-text-primary, #0F172A); }
.h-ilg__grantb small { display: block; color: var(--h-text-secondary, #64748B); font-size: 12px; margin-top: 2px; line-height: 1.4; }

.h-ilg__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-ilg__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }

.h-ilg__callout { font-size: 12.5px; line-height: 1.5; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); padding: 11px 13px; margin-bottom: 16px; }
.h-ilg__callout b { color: var(--h-text-primary, #0F172A); }
.h-ilg__callout--info { background: var(--h-info-bg, #E0F2FE); border-color: var(--h-info-border, #BAE6FD); color: var(--h-info-text, #075985); margin-top: 14px; }
.h-ilg__callout--info b { color: var(--h-info-text, #075985); }

/* toggle (mirrors plan editor) */
.h-ilg__switch { position: relative; display: flex; align-items: center; gap: 12px; cursor: pointer; margin-bottom: 8px; }
.h-ilg__switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.h-ilg__track { width: 38px; height: 22px; background: var(--h-border-strong, #CBD5E1); border-radius: 999px; position: relative; transition: background 0.15s; flex: none; }
.h-ilg__track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform 0.15s; }
.h-ilg__switch input:checked + .h-ilg__track { background: var(--h-accent, #0EA5E9); }
.h-ilg__switch input:checked + .h-ilg__track::after { transform: translateX(16px); }
.h-ilg__switch input:focus-visible + .h-ilg__track { box-shadow: var(--h-focus-ring); }
.h-ilg__swlbl { font-size: 13.5px; color: var(--h-text-primary, #0F172A); }

.h-ilg__saveerr { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); }

/* success */
.h-ilg__done { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.h-ilg__doneic { width: 38px; height: 38px; border-radius: 10px; background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); display: flex; align-items: center; justify-content: center; flex: none; }
.h-ilg__doneic svg { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 3; }
.h-ilg__donet { font-size: 14px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-ilg__doned { font-size: 12.5px; color: var(--h-text-secondary, #64748B); margin-top: 2px; }
.h-ilg__doneactions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }

/* buttons (shared token pass) */
.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary, #0F172A); }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
.h-ilg__spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: h-ilg-spin 0.7s linear infinite; }
@keyframes h-ilg-spin { to { transform: rotate(360deg); } }

/* FormKit genesis → Hermes token pass (scoped) */
.h-ilg :deep(.formkit-outer) { margin-bottom: 16px; }
.h-ilg :deep(.formkit-label) { display: block; font-size: 13px; font-weight: 500; color: var(--h-text-primary, #0F172A); margin-bottom: 6px; }
.h-ilg :deep(.formkit-input) {
  width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff);
  border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A);
  border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box;
}
.h-ilg :deep(.formkit-input:focus) { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-ilg :deep(select.formkit-input) { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M4 6l4 4 4-4'/></svg>"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 34px; }
.h-ilg :deep(textarea.formkit-input) { resize: vertical; }
.h-ilg :deep(.formkit-help) { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 6px; }
.h-ilg :deep(.formkit-message) { font-size: 12.5px; color: var(--h-danger-text, #B91C1C); margin-top: 6px; }
</style>
